import { Box, Stack } from "@mui/material";
import { MonthIndicatorDots } from "@src/appV2/redesign/Shift/Calendar/MonthIndicatorDots";
import { MonthNavigationHeader } from "@src/appV2/redesign/Shift/Calendar/MonthNavigationHeader";
import { useMonthNavigation } from "@src/appV2/redesign/Shift/Calendar/useMonthNavigation";
import type { ShiftsByDateAndTimeSlot } from "@src/appV2/redesign/utils/groupShiftsByDateAndTimeSlot";
import SwipeableViews from "react-swipeable-views";

import { CalendarHeaderRow } from "../../Calendar/HeaderRow";
import { ShiftCalendarCore } from "./ShiftCalendarCore";
import type { OpenShiftDatesResponseWithUrgent } from "./types";

interface SwipeableShiftCalendarProps {
  startDate: Date;
  endDate: Date;
  maxMonths?: number;
  isLoading: boolean;
  filteredOpenShiftsCountData?: OpenShiftDatesResponseWithUrgent;
  workerShiftsByDate?: ShiftsByDateAndTimeSlot;
  selectedDates: string[];
  onDateToggle: (date: string, isSelected: boolean) => void;
  loadMore?: (date: Date) => void;
  showPriorityShifts?: boolean;
  showUrgentShifts?: boolean;
  showNavigationHeader?: boolean;
  showIndicatorDots?: boolean;
}

export function SwipeableShiftCalendar(props: SwipeableShiftCalendarProps) {
  const {
    startDate,
    endDate,
    maxMonths = 6,
    isLoading,
    filteredOpenShiftsCountData,
    workerShiftsByDate,
    selectedDates,
    onDateToggle,
    loadMore,
    showPriorityShifts = false,
    showUrgentShifts = false,
    showNavigationHeader = true,
    showIndicatorDots = true,
  } = props;

  const {
    currentMonthIndex,
    allMonthDates,
    currentMonthDate,
    isFirstMonth,
    isLastMonth,
    isMonthInLoadedRange,
    handlePreviousMonth,
    handleNextMonth,
    handleMonthChange,
  } = useMonthNavigation({
    startDate,
    endDate,
    loadMore,
    maxMonths,
  });

  return (
    <Stack spacing={4} sx={{ pb: showIndicatorDots ? 0 : 4 }}>
      {showNavigationHeader && (
        <MonthNavigationHeader
          currentMonthDate={currentMonthDate}
          isFirstMonth={isFirstMonth}
          isLastMonth={isLastMonth}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
        />
      )}

      <CalendarHeaderRow />

      <Box sx={{ overflow: "hidden" }}>
        <SwipeableViews
          enableMouseEvents
          index={currentMonthIndex}
          style={{
            height: "auto",
          }}
          containerStyle={{
            height: "auto",
          }}
          onChangeIndex={handleMonthChange}
        >
          {allMonthDates.map((monthDate) => (
            <Box key={monthDate.toISOString()} sx={{ padding: 0 }}>
              <ShiftCalendarCore
                startDate={monthDate}
                showMonthTitle={false}
                weekSpacing={1}
                isLoading={isLoading}
                filteredOpenShiftsCountData={filteredOpenShiftsCountData}
                workerShiftsByDate={workerShiftsByDate}
                selectedDates={selectedDates}
                isMonthInLoadedRange={isMonthInLoadedRange}
                showPriorityShifts={showPriorityShifts}
                showUrgentShifts={showUrgentShifts}
                onDateToggle={onDateToggle}
              />
            </Box>
          ))}
        </SwipeableViews>
      </Box>

      {showIndicatorDots && (
        <MonthIndicatorDots allMonthDates={allMonthDates} currentMonthIndex={currentMonthIndex} />
      )}
    </Stack>
  );
}
